import React from "react";

import {
  createAccordionItems,
  isFootprintReadOnly,
  useGetCarbonProgramByFarmId,
  useGetFootprintingPorkFootprint,
  useNavigate,
  useTranslation,
  useWasAutofilled,
} from "utils";

import { FormFlowScreen, FormPage } from "components";

import { AutofillBanner } from "pages/app/footprinting/components/AutofillBanner";
import { PorkFeedInformationForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkFeedInformationForm";
import { GettingStartedForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkGettingStartedForm";
import { PorkHerdDetailsForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkHerdDetailsForm";
import { PorkManureAdditiveForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkManureAdditiveForm";
import { PorkManureStorageForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkManureStorageForm";
import { PorkMoorlandForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkMoorlandForm";
import { PorkPigletSourcingForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkPigletSourcingForm";
import { PorkStableTypeForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkStableTypeForm";
import { PorkYieldForm } from "pages/app/footprinting/components/formFlows/porkFlow/PorkYieldForm";
import { PorkCommentForm } from "pages/app/footprinting/components/formFlows/shared/CommentForm";

interface PorkFlowProps {
  farmId: string;
  year: number;
  initialStep?: number;
  editMode?: boolean;
  enablePageTransitions?: boolean;
}

const porkFormCategories = {
  animals: "footprinting:footprintingLandingPage.assessmentCategories.pork.animalsBreed",
  feed: "footprinting:footprintingLandingPage.assessmentCategories.pork.feed",
  manure: "footprinting:footprintingLandingPage.assessmentCategories.pork.manure",
};

export const PorkFormFlow = ({ farmId, year, initialStep, editMode, enablePageTransitions }: PorkFlowProps) => {
  const { t } = useTranslation("footprintingPork");
  const navigate = useNavigate();
  const porkProgressQuery = useGetFootprintingPorkFootprint(farmId, year);
  const readOnly = isFootprintReadOnly(porkProgressQuery.data);
  const carbonProgramQuery = useGetCarbonProgramByFarmId(farmId);
  const wasAutofilled = useWasAutofilled("pork", farmId, year);
  const [currentStep, setCurrentStep] = React.useState<number | undefined>(initialStep ?? 0);

  const getPageStatus = (pageKey: string) => {
    if (porkProgressQuery.data?.pagesCompleted.includes(pageKey)) return "complete";
    if (porkProgressQuery.data?.pagesIncomplete.includes(pageKey)) return "incomplete";
    return null;
  };

  const porkFlowSteps: FormPage[] = [
    {
      navigationTitle: t("footprinting:navigation.pork.gettingStarted"),
      key: GettingStartedForm.Key,
      status: getPageStatus(GettingStartedForm.Key),
      pageSubtitle: t("porkGettingStartedForm.subTitle"),
      body: () => <GettingStartedForm primaryDataId={porkProgressQuery.data?.footprint?.id} />,
      accordionFooter: wasAutofilled ? <AutofillBanner /> : undefined,
    },
    {
      navigationTitle: t("footprinting:navigation.pork.yield"),
      key: PorkYieldForm.Key,
      status: getPageStatus(PorkYieldForm.Key),
      pageSubtitle: t("porkYieldForm.subTitle"),
      body: () => <PorkYieldForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "yield"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.herdDetails"),
      navigationCategory: t(porkFormCategories.animals),
      key: PorkHerdDetailsForm.Key,
      status: getPageStatus(PorkHerdDetailsForm.Key),
      pageSubtitle: t("porkHerdDetailsForm.subTitle"),
      body: () => <PorkHerdDetailsForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "herdDetails"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.pigletSourcing"),
      navigationCategory: t(porkFormCategories.animals),
      key: PorkPigletSourcingForm.Key,
      status: getPageStatus(PorkPigletSourcingForm.Key),
      pageSubtitle: t("porkPigletSourcingForm.subTitle"),
      body: () => (
        <PorkPigletSourcingForm
          readOnly={readOnly}
          primaryDataId={porkProgressQuery.data?.footprint?.id}
          farmId={farmId}
          year={year}
          onNavigateToStep={setCurrentStep}
        />
      ),
      accordionItems: createAccordionItems(t, "footprintingPork", "pigletSourcing"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.stableType"),
      navigationCategory: t(porkFormCategories.animals),
      key: PorkStableTypeForm.Key,
      status: getPageStatus(PorkStableTypeForm.Key),
      pageSubtitle: t("porkStableTypeForm.subTitle"),
      body: () => <PorkStableTypeForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "stableType"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.feedInformation"),
      navigationCategory: t(porkFormCategories.feed),
      key: PorkFeedInformationForm.Key,
      status: getPageStatus(PorkFeedInformationForm.Key),
      pageSubtitle: t("porkFeedInformationForm.subTitle"),
      body: () => <PorkFeedInformationForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "feedInformation"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.manureStorage"),
      navigationCategory: t(porkFormCategories.manure),
      key: PorkManureStorageForm.Key,
      status: getPageStatus(PorkManureStorageForm.Key),
      pageSubtitle: t("porkManureStorageForm.subTitle"),
      body: () => <PorkManureStorageForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "manureStorage"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.manureAdditive"),
      navigationCategory: t(porkFormCategories.manure),
      key: PorkManureAdditiveForm.Key,
      status: getPageStatus(PorkManureAdditiveForm.Key),
      pageSubtitle: t("porkManureAdditiveForm.subTitle"),
      body: () => <PorkManureAdditiveForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "manureAdditive"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.moorland"),
      key: PorkMoorlandForm.Key,
      status: getPageStatus(PorkMoorlandForm.Key),
      pageSubtitle: t("porkMoorlandForm.info"),
      body: () => <PorkMoorlandForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
      accordionItems: createAccordionItems(t, "footprintingPork", "moorland"),
    },
    {
      navigationTitle: t("footprinting:navigation.pork.comment"),
      key: PorkCommentForm.Key,
      status: getPageStatus(PorkCommentForm.Key),
      pageSubtitle: t("footprinting:commentForm.subTitle"),
      body: () => <PorkCommentForm readOnly={readOnly} primaryDataId={porkProgressQuery.data?.footprint?.id} farmId={farmId} year={year} />,
    },
  ];

  // Carbon programs can opt out of specific steps via excludedFormSteps. Excluded steps
  // are filtered here so they never render, and the server mirrors this by excluding
  // them from completion stats. To exclude a step, add its FormStepId to the carbon
  // program's excludedFormSteps array in consts/carbonPrograms.ts on the API.
  const excludedSteps = new Set(carbonProgramQuery.data?.farmLevelFootprinting.excludedFormSteps ?? []);
  const enabledSteps = porkFlowSteps.filter((step) => !excludedSteps.has(step.key));

  return (
    <FormFlowScreen
      formPages={enabledSteps}
      initialStep={currentStep}
      currentStep={currentStep}
      onStepChange={setCurrentStep}
      completionPercent={porkProgressQuery.data?.completionPct ?? 0}
      navBarTitle={t("footprinting:navigation.pork.navBarTitle", { year })}
      editMode={editMode}
      onClose={() => navigate("/footprinting", { state: { year } })}
      isLoading={porkProgressQuery.isLoading}
      enablePageTransitions={enablePageTransitions}
    />
  );
};
